import { Field } from './ui.jsx';
import MapPicker from './LazyMapPicker.jsx';
import { STATES, citiesOf } from '../lib/locations.js';

/**
 * Location block: a Google-Maps-style picker (search + pin) that saves
 * latitude, longitude and a formatted address, backed by State → City dropdowns
 * that stay authoritative for nurse↔patient matching.
 *
 * Selecting a point on the map reverse-geocodes it and, when the result matches
 * our master data, auto-fills State and City; the dropdowns remain editable so
 * the two never disagree. `value` is `{ state, city, address, lat, lng }` and
 * `onChange(patch)` merges partial updates.
 */
export default function LocationFields({ value, onChange, required = true, idPrefix = 'loc', showMap = true }) {
  const cities = citiesOf(value.state);
  const mark = required ? ' *' : '';

  const setState = (state) => {
    const patch = { state };
    if (value.city && !citiesOf(state).includes(value.city)) patch.city = '';
    onChange(patch);
  };

  // Reconcile a map selection with the dropdown master data.
  const fromMap = ({ lat, lng, address, city, state }) => {
    const patch = { lat, lng };
    if (address != null) patch.address = address;
    if (state && STATES.includes(state)) {
      patch.state = state;
      if (city && citiesOf(state).includes(city)) patch.city = city;
      else if (value.city && !citiesOf(state).includes(value.city)) patch.city = '';
    }
    onChange(patch);
  };

  return (
    <>
      {showMap && (
        <Field label={`Pin your location${mark}`} hint="Search or tap the map — we save the exact point.">
          <MapPicker value={{ lat: value.lat, lng: value.lng, address: value.address }} onChange={fromMap} />
        </Field>
      )}

      <div className="grid grid-2">
        <Field label={`State${mark}`} id={`${idPrefix}-state`}>
          <select
            id={`${idPrefix}-state`}
            value={value.state || ''}
            onChange={(e) => setState(e.target.value)}
            required={required}
          >
            <option value="">Select a state…</option>
            {STATES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label={`City${mark}`}
          id={`${idPrefix}-city`}
          hint={!value.state ? 'Choose a state first' : undefined}
        >
          <select
            id={`${idPrefix}-city`}
            value={value.city || ''}
            onChange={(e) => onChange({ city: e.target.value })}
            required={required}
            disabled={!value.state}
          >
            <option value="">{value.state ? 'Select a city…' : '—'}</option>
            {cities.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field
        label="Local area / address"
        id={`${idPrefix}-area`}
        hint="Auto-filled from the map — refine it with a flat number or landmark."
      >
        <input
          id={`${idPrefix}-area`}
          type="text"
          value={value.address || ''}
          onChange={(e) => onChange({ address: e.target.value })}
          placeholder="e.g. Panampilly Nagar, near the metro station"
        />
      </Field>
    </>
  );
}
